<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { usePlanningApi } from '../composables/usePlanningApi'

/**
 * Material availability from the real BOM and stock ledger.
 *
 * For every active order the BOM materials (`/bom?product_id=` → `/bom/{id}`)
 * are scaled by the order's planned quantity and compared against stock on hand
 * summed across warehouses (`/inventory/balances?product_id=`). Both endpoints
 * must be permitted (`bom.view`, `inventory.view`).
 */

const { t } = useI18n()
const {
  listAllProductionOrders,
  listBomVersions,
  getBomVersion,
  listMaterialBalances,
} = usePlanningApi()

interface RequirementRow {
  material_id: number
  material_name: string
  material_sku: string | null
  unit: string | null
  required: number
  available: number
  orders: string[]
}

const rows = ref<RequirementRow[]>([])
const loading = ref(true)
const error = ref('')
const warnings = ref<string[]>([])
const ordersChecked = ref(0)
const ordersWithoutBom = ref<number>(0)

const shortages = computed(() => rows.value.filter((row) => row.available < row.required))

async function load() {
  loading.value = true
  error.value = ''
  warnings.value = []
  try {
    const orders = (await listAllProductionOrders({})).filter(
      (order) => order.status === 'planned' || order.status === 'in_progress',
    )
    ordersChecked.value = orders.length

    const byProduct = new Map<number, typeof orders>()
    for (const order of orders) {
      byProduct.set(order.product_id, [...(byProduct.get(order.product_id) ?? []), order])
    }

    const collected = new Map<number, RequirementRow>()
    let missingBom = 0

    for (const [productId, productOrders] of byProduct) {
      const versions = await listBomVersions(productId)
      const version = versions.find((entry) => Boolean(entry.is_active)) ?? versions[0]
      if (!version) {
        missingBom += 1
        warnings.value.push(t('planning.materials.bomMissing', { product: productOrders[0]?.product_name || `#${productId}` }))
        continue
      }

      const detail = await getBomVersion(version.id)
      const materials = (detail.operations ?? []).flatMap((operation) => operation.materials ?? [])
      const plannedTotal = productOrders.reduce((sum, order) => sum + Number(order.planned_quantity ?? 0), 0)

      for (const material of materials) {
        const required =
          Number(material.quantity ?? 0) * (1 + Number(material.loss_percentage ?? 0) / 100) * plannedTotal
        const existing = collected.get(material.material_id)
        const row =
          existing ?? {
            material_id: material.material_id,
            material_name: material.material_name,
            material_sku: material.material_sku,
            unit: material.material_unit,
            required: 0,
            available: 0,
            orders: [],
          }
        row.required += required
        row.orders.push(...productOrders.map((order) => order.mo_code))
        collected.set(material.material_id, row)
      }
    }

    // Stock on hand per material, summed across warehouses.
    for (const row of collected.values()) {
      try {
        const balances = await listMaterialBalances(row.material_id)
        row.available = balances.reduce((sum, balance) => sum + Number(balance.balance ?? 0), 0)
      } catch {
        row.available = 0
      }
    }

    ordersWithoutBom.value = missingBom
    rows.value = [...collected.values()].sort((a, b) => (b.required - b.available) - (a.required - a.available))
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function number(value: number): string {
  return Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.eyebrow') }}</p>
      <h2>{{ t('planning.materials.title') }}</h2>
      <p>{{ t('planning.materials.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
    <span class="spacer" />
    <div class="summary">
      {{ t('planning.materials.ordersChecked', { count: ordersChecked }) }}
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <template v-else>
    <section v-if="shortages.length" class="notice-panel">
      <strong>{{ t('planning.materials.shortages', { count: shortages.length }) }}</strong>
      <span>{{ t('planning.materials.shortagesText') }}</span>
    </section>

    <section v-if="warnings.length" class="notice-panel">
      <strong>{{ t('planning.materials.warnings', { count: ordersWithoutBom }) }}</strong>
      <span v-for="warning in warnings" :key="warning">{{ warning }}</span>
    </section>

    <section v-if="rows.length === 0" class="state-message">
      <p>{{ t('planning.materials.noRequirements') }}</p>
    </section>

    <section v-else class="table-shell">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('planning.materials.material') }}</th>
            <th class="cell-num">{{ t('planning.materials.required') }}</th>
            <th class="cell-num">{{ t('planning.materials.available') }}</th>
            <th class="cell-num">{{ t('planning.common.status') }}</th>
            <th>{{ t('planning.materials.linkedOrders') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.material_id">
            <td class="cell-name">
              {{ row.material_name }}
              <span v-if="row.material_sku" class="cell-sub">{{ row.material_sku }}</span>
            </td>
            <td class="cell-num">{{ number(row.required) }} {{ row.unit || '' }}</td>
            <td class="cell-num">{{ number(row.available) }} {{ row.unit || '' }}</td>
            <td class="cell-num">
              <span
                class="status-badge"
                :class="row.available >= row.required ? 'status-ok' : 'status-critical'"
              >
                {{ row.available >= row.required
                  ? t('planning.materials.ready')
                  : t('planning.materials.short') }}
              </span>
            </td>
            <td>{{ [...new Set(row.orders)].join(', ') }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <p class="empty-inline">{{ t('planning.materials.scopeNote') }}</p>
  </template>
</template>
